import { useEffect, useRef, useState } from "react";
import { api } from "../lib/api";

// Bloc ADRESSE avec autocomplétion (demande du propriétaire, 2026-11-03) :
// « une fois le code postal entré, demander le no de porte et suggérer la ou les
// adresses pour les remplir automatiquement ».
// ORDRE : code postal EN PREMIER (→ ville + province suggérées/remplies), puis
// N° de porte + rue avec autocomplétion Geoapify (un clic remplit tout).
// Le parent garde un seul champ address1 (« N° rue ») — rien ne change côté données.
export function AddressAutocomplete({ lang, form, setForm, inputCls, labelCls, PROVINCES }) {
  const fr = lang !== "en";
  const [sugg, setSugg] = useState([]);          // suggestions d'adresses à la frappe
  const [cpSugg, setCpSugg] = useState([]);      // suggestions ville/province par code postal
  const [addrQuery, setAddrQuery] = useState(""); // texte tapé dans le champ rue
  const [noPorte, setNoPorte] = useState("");     // N° de porte (combiné dans address1)
  const debounceRef = useRef(null);
  const cpLoadedRef = useRef("");

  // Combine « N° + rue » dans le champ address1 du parent (compatibilité données)
  const combine = (no, rue) => {
    const a = [no, rue].filter(Boolean).join(" ").trim();
    setForm((f) => ({ ...f, address1: a }));
  };

  // Initialiser N° / rue depuis address1 existant (préremplissage compte)
  useEffect(() => {
    if (form.address1 && !noPorte && !addrQuery) {
      const m = String(form.address1).match(/^\s*(\d+[A-Za-z]?)\s+(.*)$/);
      if (m) { setNoPorte(m[1]); setAddrQuery(m[2]); }
      else setAddrQuery(form.address1);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Code postal → ville + province suggérées (auto-remplit si une seule ville)
  const onCpChange = (e) => {
    const v = e.target.value.toUpperCase();
    setForm((f) => ({ ...f, postal_code: v }));
    const norm = v.replace(/[^A-Z0-9]/g, "");
    if (/^[A-Z]\d[A-Z]\d[A-Z]\d$/.test(norm)) {
      if (cpLoadedRef.current === norm) return;
      cpLoadedRef.current = norm;
      api.get("/geo/cp", { params: { cp: norm } })
        .then(({ data }) => {
          const s = data.suggestions || [];
          setCpSugg(s);
          if (s.length === 1) {
            setForm((f) => ({ ...f, city: s[0].city, province: s[0].province || f.province }));
            setCpSugg([]);
          } else if (s.length > 1 && !form.city) {
            setForm((f) => ({ ...f, city: s[0].city, province: s[0].province || f.province }));
          }
        })
        .catch(() => setCpSugg([]));
    } else {
      setCpSugg([]);
      cpLoadedRef.current = "";
    }
  };

  // Autocomplétion de la rue (déclenchée dès 3 caractères, débattue 300 ms)
  const onRueChange = (e) => {
    const v = e.target.value;
    setAddrQuery(v);
    combine(noPorte, v);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    const q = [noPorte, v].filter(Boolean).join(" ").trim();
    if (q.length < 3) { setSugg([]); return; }
    debounceRef.current = setTimeout(() => {
      api.get("/geo/adresse", { params: { q, cp: form.postal_code } })
        .then(({ data }) => setSugg(data.suggestions || []))
        .catch(() => setSugg([]));
    }, 300);
  };

  const onNoPorteChange = (e) => {
    const v = e.target.value;
    setNoPorte(v);
    combine(v, addrQuery);
    // Relance l'autocomplétion avec le N° (meilleure suggestion)
    if (debounceRef.current) clearTimeout(debounceRef.current);
    const q = [v, addrQuery].filter(Boolean).join(" ").trim();
    if (q.length >= 3) {
      debounceRef.current = setTimeout(() => {
        api.get("/geo/adresse", { params: { q, cp: form.postal_code } })
          .then(({ data }) => setSugg(data.suggestions || []))
          .catch(() => setSugg([]));
      }, 300);
    }
  };

  const choisir = (s) => {
    setNoPorte(s.housenumber || noPorte);
    setAddrQuery(s.street || addrQuery);
    setForm((f) => ({
      ...f,
      address1: [s.housenumber, s.street].filter(Boolean).join(" ").trim() || f.address1,
      city: s.city || f.city,
      province: s.province || f.province,
      postal_code: (s.postal_code || f.postal_code).toUpperCase(),
    }));
    setSugg([]);
    setCpSugg([]);
  };

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  return (
    <>
      {/* 1. CODE POSTAL en premier (demande du propriétaire) */}
      <div>
        <label htmlFor="qq-postal" className={labelCls}>{fr ? "Code postal" : "Postal code"} *</label>
        <input id="qq-postal" data-testid="quote-postal-code" required minLength={6} maxLength={7}
          placeholder="A1A 1A1" name="code_postal"
          value={form.postal_code} onChange={onCpChange} className={inputCls} />
        {/* Ville(s) suggérée(s) par le code postal */}
        {cpSugg.length > 0 && (
          <div className="mt-2 rounded-lg border border-gold/30 bg-gold/5 p-2 space-y-1" data-testid="cp-suggestions">
            {cpSugg.map((s, i) => (
              <button key={i} type="button" onClick={() => { setForm((f) => ({ ...f, city: s.city, province: s.province || f.province })); setCpSugg([]); }}
                data-testid={`cp-suggestion-${i}`}
                className="block w-full text-left text-xs px-2 py-1.5 rounded hover:bg-gold/15 text-ivory/85 transition-colors">
                {s.city}{s.province ? `, ${s.province}` : ""}
              </button>
            ))}
          </div>
        )}
      </div>
      {/* 2. VILLE (auto-remplie par le code postal) */}
      <div>
        <label htmlFor="qq-city" className={labelCls}>{fr ? "Ville" : "City"} *</label>
        <input id="qq-city" data-testid="quote-city" required minLength={2} maxLength={100}
          name="ville" value={form.city} onChange={set("city")} className={inputCls} />
      </div>
      {/* 3. PROVINCE */}
      <div>
        <label htmlFor="qq-province" className={labelCls}>{fr ? "Province" : "Province"} *</label>
        <select id="qq-province" data-testid="quote-province" required name="province"
          value={form.province} onChange={set("province")} className={inputCls}>
          <option value="" disabled>{fr ? "Choisir la province…" : "Select a province…"}</option>
          {PROVINCES.map((p) => (
            <option key={p.v} value={p.v}>{fr ? p.fr : p.en}</option>
          ))}
        </select>
      </div>
      {/* 4. N° DE PORTE (demande du propriétaire : demandé après le code postal) */}
      <div>
        <label htmlFor="qq-noporte" className={labelCls}>{fr ? "N° de porte" : "Street number"} *</label>
        <input id="qq-noporte" data-testid="quote-no-porte" required minLength={1} maxLength={10}
          placeholder={fr ? "ex. 1314" : "e.g. 1314"} name="no_porte" inputMode="numeric"
          value={noPorte} onChange={onNoPorteChange} className={inputCls} />
      </div>
      {/* 5. RUE avec AUTOCOMPLÉTION (suggère l'adresse complète) */}
      <div className="sm:col-span-2 relative">
        <label htmlFor="qq-address" className={labelCls}>{fr ? "Rue (suggestions automatiques)" : "Street (automatic suggestions)"} *</label>
        <input id="qq-address" data-testid="quote-address" required minLength={3} maxLength={200}
          name="adresse" autoComplete="off"
          placeholder={fr ? "Commencez à taper votre rue…" : "Start typing your street…"}
          value={addrQuery} onChange={onRueChange} className={inputCls} />
        {sugg.length > 0 && (
          <ul className="absolute z-20 left-0 right-0 mt-1 rounded-lg border border-gold/30 bg-ink shadow-xl overflow-hidden"
            data-testid="adresse-suggestions">
            {sugg.map((s, i) => (
              <li key={i}>
                <button type="button" onClick={() => choisir(s)}
                  data-testid={`adresse-suggestion-${i}`}
                  className="block w-full text-left px-4 py-2.5 text-sm text-ivory/90 hover:bg-gold/15 transition-colors">
                  {s.formatted || [s.housenumber, s.street, s.city, s.province, s.postal_code].filter(Boolean).join(", ")}
                </button>
              </li>
            ))}
            <li className="px-4 py-1.5 text-[10px] text-ivory/35 border-t border-ivory/10">
              <a href="https://www.geoapify.com/" target="_blank" rel="noreferrer" className="hover:text-gold">Powered by Geoapify</a>
            </li>
          </ul>
        )}
      </div>
    </>
  );
}
